
import { Component, Vue } from "vue-property-decorator";
import Sidebar from "@/components/navigations/Sidebar.vue";
import Cta from "@/components/buttons/Button.vue";
import { toast } from "@/store";
import LoginService from "@/services/login.service";
import { LocalStorageApp } from "@/utils/localstorage";
import { PASSWORD_REQUIREMENTS, isPasswordStrong } from "@/helpers";

@Component({
  components: { Sidebar, Cta },
})
/**
 * Forced password-change gate. The router redirects here whenever the password used at login
 * doesn't meet the org's strength policy — see the `pcp.mustChangePassword` local-storage flag
 * set by `Login.vue`/`Unlock.vue` and read by the navigation guard in `router/index.ts`.
 */
export default class PasswordSecurity extends Vue {
  private passwdActuel = "";
  private passwdNew = "";
  private passwdNewConfirm = "";

  /** Requirement checks, unlit until the user has typed at least one character. */
  get requirements(): { label: string; valid: boolean }[] {
    return PASSWORD_REQUIREMENTS.map((requirement) => ({
      label: requirement.label,
      valid: !!this.passwdNew && requirement.test(this.passwdNew),
    }));
  }

  /** Whether the confirmation matches the new password, unlit until the user has typed something. */
  get passwordsMatch(): boolean {
    return !!this.passwdNewConfirm && this.passwdNewConfirm === this.passwdNew;
  }

  /**
   * Validates and submits the new password: all fields filled, matches its confirmation, differs
   * from the current one, and meets the strength policy. Clears the forced-change flag on success.
   */
  private async validUpdatePasswd(): Promise<void> {
    if (!this.passwdActuel || !this.passwdNew || !this.passwdNewConfirm) {
      toast.pushError("Merci de remplir l'ensemble des champs");
      return;
    }
    if (this.passwdNew !== this.passwdNewConfirm) {
      toast.pushError(
        "Votre nouveau mot de passe n'est pas identique à la confirmation"
      );
      return;
    }
    if (this.passwdActuel === this.passwdNew) {
      toast.pushError(
        "Votre nouveau mot de passe doit être différent de celui actuel"
      );
      return;
    }
    if (!isPasswordStrong(this.passwdNew)) {
      toast.pushError(
        "Votre nouveau mot de passe ne respecte pas encore l'ensemble des règles de sécurité"
      );
      return;
    }
    if (
      await LoginService.changePassword(
        this.passwdActuel,
        this.passwdNew,
        this.passwdNewConfirm
      )
    ) {
      LocalStorageApp.setItem("pcp.mustChangePassword", false);
      const go = this.$route.query.go as string | undefined;
      this.$router.replace(go || "/accueil");
    }
  }
}
